import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from 'src/context/auth/AuthContext';
import { BUILT_IN_ROLES } from 'src/config/permissions';
import { roleService, RoleInput, RoleMember, RoleUpdate } from 'src/services/role.service';
import { RoleDefinition } from 'src/types/permissions.types';

const HIERARCHY = ['SYSTEM_CREATOR', 'ADMIN', 'TEACHER', 'BANKER', 'STUDENT'];

/** Roles, people and every role mutation the Roles screens need. */
export function useRolesAdmin() {
  const queryClient = useQueryClient();
  const { refreshRoles } = useAuth();

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => roleService.getRoles() });
  const membersQuery = useQuery({ queryKey: ['role-members'], queryFn: () => roleService.getMembers() });

  // Before the migration the table is empty; the built-ins keep the screens meaningful.
  const stored = rolesQuery.data && rolesQuery.data.length > 0 ? rolesQuery.data : BUILT_IN_ROLES;
  // Built-ins in hierarchy order (System Creator first), then custom roles by name.
  const rank = (role: RoleDefinition) => {
    const index = HIERARCHY.indexOf(role.name);
    return index === -1 ? HIERARCHY.length : index;
  };
  const roles: RoleDefinition[] = [...stored].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
  const members: RoleMember[] = membersQuery.data || [];

  const membersByRole = useMemo(() => {
    const map = new Map<string, RoleMember[]>();
    members.forEach((m) => {
      const key = (m.role || '').toUpperCase();
      map.set(key, [...(map.get(key) || []), m]);
    });
    return map;
  }, [members]);

  const afterRoleChange = async () => {
    await queryClient.invalidateQueries({ queryKey: ['roles'] });
    await refreshRoles();
  };

  const createRole = useMutation({
    mutationFn: (input: RoleInput) => roleService.createRole(input),
    onSuccess: async (role) => {
      toast.success(`${role.label} role created`);
      await afterRoleChange();
    },
  });

  const updateRole = useMutation({
    mutationFn: ({ name, patch }: { name: string; patch: RoleUpdate }) => roleService.updateRole(name, patch),
    onSuccess: async (role) => {
      toast.success(`${role.label} saved`);
      await afterRoleChange();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteRole = useMutation({
    mutationFn: (role: RoleDefinition) => roleService.deleteRole(role),
    onSuccess: async () => {
      toast.success('Role deleted');
      await afterRoleChange();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const assignRole = useMutation({
    mutationFn: ({ userId, roleName }: { userId: string; roleName: string }) => roleService.assignRole(userId, roleName),
    onSuccess: async (member) => {
      const label = roles.find((r) => r.name === member.role)?.label || member.role;
      toast.success(`${member.firstname || member.email} is now ${label}`);
      await queryClient.invalidateQueries({ queryKey: ['role-members'] });
    },
  });

  return {
    roles,
    members,
    membersByRole,
    membersOf: (roleName: string) => membersByRole.get(roleName.toUpperCase()) || [],
    isLoading: rolesQuery.isLoading || membersQuery.isLoading,
    error: rolesQuery.error || membersQuery.error,
    createRole,
    updateRole,
    deleteRole,
    assignRole,
  };
}
